import { useState, useEffect, useCallback, useRef } from 'react';
import { useCheckoutStore } from '../store/checkout';
import { Button } from '../components/Button';
import styles from './PaymentStep.module.css';
import type { SurfboardPaymentMethod, PaymentStatusData } from '../types/surfboard';
import { 
  getSavedTokens, 
  deleteToken, 
  initiateMITPayment,
  type SavedToken 
} from '../api/client';

// Payment status descriptions for user feedback
const statusMessages: Record<string, { title: string; description: string }> = {
  PAYMENT_INITIATED: { title: 'Starter betaling...', description: 'Vennligst vent mens vi starter betalingen.' },
  PAYMENT_PROCESSING: { title: 'Behandler betaling...', description: 'Din betaling blir behandlet. Ikke lukk dette vinduet.' },
  PAYMENT_PROCESSED: { title: 'Behandlet', description: 'Betalingen er behandlet. Fullfører...' },
  PAYMENT_COMPLETED: { title: 'Betaling fullført!', description: 'Takk for din betaling.' },
  PAYMENT_FAILED: { title: 'Betaling feilet', description: 'Noe gikk galt med betalingen. Prøv igjen.' },
  PAYMENT_CANCELLED: { title: 'Betaling avbrutt', description: 'Betalingen ble avbrutt.' },
};

type ActivePaymentMethod = 'card' | 'saved-card' | 'klarna' | 'vipps' | 'swish' | null;

export function PaymentStep() {
  const { 
    setStep, 
    cart, 
    surfboardOrderId, 
    surfboardNonce, 
    publicKey,
    customer,
    shippingAddress,
    merchant,
    sessionId,
    completeCheckout
  } = useCheckoutStore();

  const [isLoading, setIsLoading] = useState(true);
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);
  const [activeMethod, setActiveMethod] = useState<ActivePaymentMethod>('card');
  const [savedTokens, setSavedTokens] = useState<SavedToken[]>([]);
  const [selectedTokenId, setSelectedTokenId] = useState<string | null>(null);
  const sdkInitialized = useRef(false);

  // Get active payment methods from merchant config
  const activatedMethods = merchant?.activatedPaymentMethods || [];
  const hasCard = activatedMethods.some(m => ['CARD', 'VISA', 'MASTERCARD'].includes(m));
  const hasKlarna = activatedMethods.includes('KLARNA');
  const hasVipps = activatedMethods.some(m => ['VIPPS', 'SVIPPS'].includes(m));
  const hasSwish = activatedMethods.some(m => ['SWISH', 'SSWISH', 'NSWISH'].includes(m));
  const hasApplePay = activatedMethods.some(m => ['APPLEPAY', 'APPLE_PAY'].includes(m)) || hasCard;
  const hasGooglePay = activatedMethods.some(m => ['GOOGLEPAY', 'GOOGLE_PAY'].includes(m)) || hasCard;

  // Fetch saved cards for this customer
  useEffect(() => {
    const fetchSavedTokens = async () => {
      if (!sessionId) return;
      
      try {
        const result = await getSavedTokens(sessionId);
        if (result.data?.tokens && result.data.tokens.length > 0) {
          setSavedTokens(result.data.tokens);
          // If user has saved cards, default to showing them
          setActiveMethod('saved-card');
          setSelectedTokenId(result.data.tokens[0].tokenId);
        }
      } catch (err) {
        console.log('[PaymentStep] No saved cards found:', err);
      }
    };
    
    fetchSavedTokens();
  }, [sessionId]);

  // Handle payment status callback
  const handlePaymentStatus = useCallback((data: PaymentStatusData) => {
    console.log('[PaymentStep] Payment status:', data);
    setProcessingStatus(data.paymentStatus);

    switch (data.paymentStatus) {
      case 'PAYMENT_COMPLETED':
      case 'PAYMENT_PROCESSED':
        // Success - notify parent via completeCheckout
        setTimeout(() => {
          setIsProcessing(false);
          completeCheckout({
            success: true,
            paymentId: data.paymentId || surfboardOrderId || '',
            orderId: surfboardOrderId || '',
            status: 'COMPLETED',
            paymentMethod: activeMethod?.toUpperCase() || 'UNKNOWN',
          });
        }, 1500);
        break;

      case 'PAYMENT_FAILED':
        setIsProcessing(false);
        setError(data.errorMessage || data.failureReason || 'Betalingen feilet. Prøv igjen.');
        completeCheckout({
          success: false,
          paymentId: data.paymentId || '',
          orderId: surfboardOrderId || '',
          status: 'FAILED',
          paymentMethod: activeMethod?.toUpperCase() || 'UNKNOWN',
          errorMessage: data.errorMessage || data.failureReason || 'Betalingen feilet',
        });
        break;

      case 'PAYMENT_CANCELLED':
        setIsProcessing(false);
        setError('Betalingen ble avbrutt.');
        completeCheckout({
          success: false,
          paymentId: '',
          orderId: surfboardOrderId || '',
          status: 'CANCELLED',
          paymentMethod: activeMethod?.toUpperCase() || 'UNKNOWN',
          errorMessage: 'Betalingen ble avbrutt',
        });
        break;

      case 'PAYMENT_INITIATED':
      case 'PAYMENT_PROCESSING':
        // Still processing - keep showing overlay
        break;
    }
  }, [completeCheckout, surfboardOrderId, activeMethod]);

  // Handle SDK errors
  const handleSdkError = useCallback((code: string, message: string) => {
    console.error('[PaymentStep] SDK Error:', code, message);
    setError(`Feil ${code}: ${message}`);
    setIsLoading(false);
    setIsProcessing(false);
  }, []);

  // Initialize Surfboard SDK
  useEffect(() => {
    const initializeSDK = async () => {
      // Check if SDK is loaded
      if (!window.SurfboardOnlineSDK) {
        setError('Betalings-SDK kunne ikke lastes. Vennligst oppdater siden.');
        setIsLoading(false);
        return;
      }

      // Check required data
      if (!surfboardOrderId || !surfboardNonce || !publicKey) {
        setError('Mangler betalingsinformasjon. Vennligst start på nytt.');
        setIsLoading(false);
        return;
      }

      // Prevent double initialization
      if (sdkInitialized.current) {
        return;
      }
      sdkInitialized.current = true;

      try {
        const SDK = window.SurfboardOnlineSDK;

        // Set up callbacks first
        SDK.errorCallback(handleSdkError);
        SDK.paymentStatusCallback(handlePaymentStatus);

        // Initialize SDK
        console.log('[PaymentStep] Initializing SDK with:', { publicKey, orderId: surfboardOrderId });
        await SDK.initialiseOnlineSDK({
          publicKey,
          orderId: surfboardOrderId,
          nonce: surfboardNonce,
        });

        // Mount widgets
        const mountOptions: { 
          mountCardWidget?: string; 
          mountApplePayWidget?: string; 
          mountGooglePayWidget?: string 
        } = {};

        if (hasCard) {
          mountOptions.mountCardWidget = 'surfboard-card-widget';
        }
        if (hasApplePay) {
          mountOptions.mountApplePayWidget = 'surfboard-applepay-widget';
        }
        if (hasGooglePay) {
          mountOptions.mountGooglePayWidget = 'surfboard-googlepay-widget';
        }

        SDK.mount(mountOptions);

        // Update customer info in SDK
        if (customer && shippingAddress) {
          const phoneMatch = customer.phone?.match(/(\+\d{1,3})\s*(.+)/);
          await SDK.order.addCustomerInformation({
            name: customer.name,
            email: customer.email,
            phone: phoneMatch ? {
              countryCode: phoneMatch[1].replace('+', ''),
              number: phoneMatch[2].replace(/\s/g, ''),
            } : undefined,
            billingAddress: {
              addressLine1: shippingAddress.streetAddress,
              city: shippingAddress.city,
              postalCode: shippingAddress.postalCode,
              countryCode: shippingAddress.countryCode || 'NO',
            },
          });
        }

        setSdkReady(true);
        setIsLoading(false);
        console.log('[PaymentStep] SDK initialized successfully');

      } catch (err) {
        console.error('[PaymentStep] SDK initialization failed:', err);
        setError('Kunne ikke initialisere betalingssystemet. Prøv igjen.');
        setIsLoading(false);
        sdkInitialized.current = false;
      }
    };

    initializeSDK();
  }, [surfboardOrderId, surfboardNonce, publicKey, customer, shippingAddress, handlePaymentStatus, handleSdkError, hasCard, hasApplePay, hasGooglePay]);

  // Initiate card payment
  const handleCardPayment = async () => {
    if (!window.SurfboardOnlineSDK || !sdkReady) return;

    setIsProcessing(true);
    setError(null);

    try {
      await window.SurfboardOnlineSDK.order.initiatePayments('CARD');
    } catch (err) {
      console.error('[PaymentStep] Card payment failed:', err);
      setError('Kortbetalingen feilet. Kontroller kortinformasjonen og prøv igjen.');
      setIsProcessing(false);
    }
  };

  // Pay with saved card token
  const handleSavedCardPayment = async () => {
    if (!selectedTokenId || !sessionId) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Use the MIT endpoint for token-based payments
      const result = await initiateMITPayment(sessionId, selectedTokenId, 'MIT_UNSCHEDULED');
      
      if (result.data?.status === 'COMPLETED' || result.data?.status === 'SUCCESS') {
        // Success - notify parent
        completeCheckout({
          success: true,
          paymentId: result.data?.paymentId || '',
          orderId: surfboardOrderId || '',
          status: 'COMPLETED',
          paymentMethod: 'CTOKEN',
        });
      } else if (result.error) {
        setError(result.error.message || 'Betaling med lagret kort feilet.');
        setIsProcessing(false);
      } else {
        setProcessingStatus('PAYMENT_PROCESSING');
      }
    } catch (err) {
      console.error('[PaymentStep] Saved card payment failed:', err);
      setError('Betaling med lagret kort feilet. Prøv et annet kort.');
      setIsProcessing(false);
    }
  };

  // Delete a saved card
  const handleDeleteToken = async (tokenId: string) => {
    if (!sessionId) return;
    
    try {
      await deleteToken(tokenId, sessionId);
      setSavedTokens(prev => prev.filter(t => t.tokenId !== tokenId));
      if (selectedTokenId === tokenId) {
        setSelectedTokenId(savedTokens.find(t => t.tokenId !== tokenId)?.tokenId || null);
      }
    } catch (err) {
      console.error('[PaymentStep] Failed to delete token:', err);
    }
  };

  // Initiate alternative payment (Klarna, Vipps, Swish)
  const handleAltPayment = async (method: SurfboardPaymentMethod) => {
    if (!window.SurfboardOnlineSDK || !sdkReady) return;

    setIsProcessing(true);
    setError(null);

    try {
      const paymentAttempt = await window.SurfboardOnlineSDK.order.initiatePayments(method);
      
      // For Swish, handle redirect if needed
      if (method === 'NSWISH' && paymentAttempt.getSwishAppRedirectUrl) {
        const redirectUrl = paymentAttempt.getSwishAppRedirectUrl();
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      }
    } catch (err) {
      console.error(`[PaymentStep] ${method} payment failed:`, err);
      setError(`${method}-betaling feilet. Prøv igjen.`);
      setIsProcessing(false);
    }
  };

  const total = cart?.totalAmount.amount ? cart.totalAmount.amount / 100 : 0;
  const currency = cart?.totalAmount.currency || 'NOK';

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p className={styles.loadingText}>Laster betalingsalternativer...</p>
        </div>
      </div>
    );
  }

  // Error state with retry
  if (error && !sdkReady) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p className={styles.errorMessage}>{error}</p>
          <button 
            className={styles.retryButton}
            onClick={() => window.location.reload()}
          >
            Prøv igjen
          </button>
        </div>
        <div className={styles.actions}>
          <Button variant="secondary" onClick={() => setStep('shipping')}>
            Tilbake
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Processing overlay */}
      {isProcessing && (
        <div className={styles.processingOverlay}>
          <div className={styles.processingSpinner} />
          <p className={styles.processingText}>
            {processingStatus ? statusMessages[processingStatus]?.title : 'Behandler betaling...'}
          </p>
          <p className={styles.processingSubtext}>
            {processingStatus ? statusMessages[processingStatus]?.description : 'Vennligst vent...'}
          </p>
        </div>
      )}

      <div className={styles.header}>
        <h2 className={styles.title}>Velg betalingsmåte</h2>
        <p className={styles.subtitle}>Alle transaksjoner er kryptert og sikre</p>
      </div>

      {/* Error message */}
      {error && (
        <div className={styles.ageNotice} style={{ background: '#fef2f2', color: '#991b1b' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          {error}
        </div>
      )}

      {/* Saved cards section */}
      {savedTokens.length > 0 && (
        <div className={`${styles.methodSection} ${activeMethod === 'saved-card' ? styles.methodSectionActive : ''}`}>
          <div className={styles.methodHeader} onClick={() => setActiveMethod('saved-card')}>
            <div className={`${styles.radio} ${activeMethod === 'saved-card' ? styles.radioSelected : ''}`} />
            <div className={styles.cardIcons}>
              <svg height="24" viewBox="0 0 24 24" fill="currentColor" opacity="0.7">
                <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
              </svg>
            </div>
            <div className={styles.methodInfo}>
              <p className={styles.methodTitle}>Lagrede kort</p>
              <p className={styles.methodDescription}>Betal med et lagret kort</p>
            </div>
          </div>

          {activeMethod === 'saved-card' && (
            <div className={styles.sdkContainer}>
              <div className={styles.savedCardsList}>
                {savedTokens.map((token) => (
                  <div 
                    key={token.tokenId}
                    className={`${styles.savedCardItem} ${selectedTokenId === token.tokenId ? styles.savedCardSelected : ''}`}
                    onClick={() => setSelectedTokenId(token.tokenId)}
                  >
                    <div className={`${styles.radio} ${selectedTokenId === token.tokenId ? styles.radioSelected : ''}`} />
                    {token.cardArt ? (
                      <img src={token.cardArt} alt={token.cardBrand} className={styles.savedCardArt} />
                    ) : (
                      <div className={styles.savedCardBrand}>
                        {token.cardBrand === 'VISA' && (
                          <svg height="20" viewBox="0 0 50 35">
                            <rect fill="#1434CB" width="50" height="35" rx="4"/>
                            <path fill="#fff" d="M19.5 23.5l1.8-11h2.9l-1.8 11h-2.9z"/>
                          </svg>
                        )}
                        {token.cardBrand === 'MASTERCARD' && (
                          <svg height="20" viewBox="0 0 50 35">
                            <rect fill="#000" width="50" height="35" rx="4"/>
                            <circle fill="#EB001B" cx="19" cy="17.5" r="10"/>
                            <circle fill="#F79E1B" cx="31" cy="17.5" r="10"/>
                          </svg>
                        )}
                        {token.cardBrand === 'AMEX' && (
                          <svg height="20" viewBox="0 0 50 35">
                            <rect fill="#006FCF" width="50" height="35" rx="4"/>
                            <path fill="#fff" d="M25 10l-2.5 6h5L25 10zm0-3l4.5 11h-2.3l-.8-2h-2.8l-.8 2h-2.3L25 7zm-12 0h2.8l2 4.5 2-4.5h2.8v11h-2.2v-7.5l-2.3 5h-1.6l-2.3-5v7.5H13V7zm22 0h6v2h-3.8v2.5h3.6v2h-3.6v2.5h3.8v2h-6V7z"/>
                          </svg>
                        )}
                        {token.cardBrand === 'DINERS' && (
                          <svg height="20" viewBox="0 0 50 35">
                            <rect fill="#0079BE" width="50" height="35" rx="4"/>
                            <circle fill="#fff" cx="25" cy="17.5" r="10" stroke="#0079BE" strokeWidth="2"/>
                            <path fill="#0079BE" d="M20 12v11M30 12v11"/>
                          </svg>
                        )}
                        {token.cardBrand === 'DISCOVER' && (
                          <svg height="20" viewBox="0 0 50 35">
                            <rect fill="#fff" width="50" height="35" rx="4" stroke="#ccc"/>
                            <circle fill="#F48120" cx="30" cy="17.5" r="8"/>
                            <text x="8" y="20" fontSize="8" fill="#000" fontWeight="bold">DISCOVER</text>
                          </svg>
                        )}
                        {!['VISA', 'MASTERCARD', 'AMEX', 'DINERS', 'DISCOVER'].includes(token.cardBrand) && (
                          <span>{token.cardBrand}</span>
                        )}
                      </div>
                    )}
                    <div className={styles.savedCardInfo}>
                      <p className={styles.savedCardNumber}>•••• {token.truncatedPan}</p>
                      <p className={styles.savedCardExpiry}>Utløper {token.expiryMonth.toString().padStart(2, '0')}/{token.expiryYear}</p>
                    </div>
                    <button 
                      className={styles.deleteCardButton}
                      onClick={(e) => { e.stopPropagation(); handleDeleteToken(token.tokenId); }}
                      title="Fjern kort"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>

              <Button 
                onClick={handleSavedCardPayment} 
                disabled={isProcessing || !selectedTokenId}
                className={styles.payButton}
              >
                {isProcessing ? 'Behandler...' : `Betal ${total.toLocaleString('nb-NO')} ${currency}`}
              </Button>

              <button 
                className={styles.useNewCardLink}
                onClick={() => setActiveMethod('card')}
              >
                Bruk et nytt kort i stedet
              </button>
            </div>
          )}
        </div>
      )}

      {/* Card payment section */}
      {hasCard && (
        <div className={`${styles.methodSection} ${activeMethod === 'card' ? styles.methodSectionActive : ''}`}>
          <div className={styles.methodHeader} onClick={() => setActiveMethod('card')}>
            <div className={`${styles.radio} ${activeMethod === 'card' ? styles.radioSelected : ''}`} />
            <div className={styles.cardIcons}>
              <svg height="24" viewBox="0 0 50 35" className={styles.cardIcon}>
                <rect fill="#1434CB" width="50" height="35" rx="4"/>
                <path fill="#fff" d="M19.5 23.5l1.8-11h2.9l-1.8 11h-2.9zm12-11l-2.7 7.5-.3-1.5-.9-4.8s-.1-1.2-1.5-1.2h-4.5l-.1.3s1.5.3 3.3 1.4l2.7 8.3h3l4.6-10h-3.6zm6.6 7.4c0-1.2 1.5-1.3 1.5-1.3l1.3.3.2-1.8s-1.2-.5-2.5-.5c-2.8 0-3.8 1.5-3.8 3.2 0 3 4.5 2.3 4.5 4.3 0 .2-.2 1.2-1.8 1.2s-2.5-.5-2.5-.5l-.3 1.9s1.2.6 2.9.6c1.6 0 4-1 4-3.5 0-3-4.5-2.6-4.5-3.9z"/>
              </svg>
              <svg height="24" viewBox="0 0 50 35" className={styles.cardIcon}>
                <rect fill="#000" width="50" height="35" rx="4"/>
                <circle fill="#EB001B" cx="19" cy="17.5" r="10"/>
                <circle fill="#F79E1B" cx="31" cy="17.5" r="10"/>
                <path fill="#FF5F00" d="M25 10.5a10 10 0 010 14 10 10 0 000-14z"/>
              </svg>
            </div>
            <div className={styles.methodInfo}>
              <p className={styles.methodTitle}>Betal med kort</p>
              <p className={styles.methodDescription}>Visa, Mastercard, American Express</p>
            </div>
          </div>

          {activeMethod === 'card' && (
            <div className={styles.sdkContainer}>
              {/* Surfboard Card Widget - SDK renders form here */}
              <div id="surfboard-card-widget" className={styles.cardWidget} />

              {/* Wallet buttons */}
              <div className={styles.walletButtons}>
                {hasApplePay && (
                  <div id="surfboard-applepay-widget" className={styles.walletButtonContainer} />
                )}
                {hasGooglePay && (
                  <div id="surfboard-googlepay-widget" className={styles.walletButtonContainer} />
                )}
              </div>

              <Button 
                onClick={handleCardPayment} 
                disabled={isProcessing}
                className={styles.payButton}
              >
                {isProcessing ? 'Behandler...' : `Betal ${total.toLocaleString('nb-NO')} ${currency}`}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Alternative payment methods */}
      {(hasKlarna || hasVipps || hasSwish) && (
        <>
          {hasCard && (
            <div className={styles.paymentDivider}>
              <span className={styles.paymentDividerText}>eller betal med</span>
            </div>
          )}

          <div className={styles.altPaymentMethods}>
            {hasKlarna && (
              <button 
                className={styles.altPaymentButton}
                onClick={() => handleAltPayment('KLARNA')}
                disabled={isProcessing}
              >
                <svg viewBox="0 0 67 36" height="28" fill="#FFB3C7" className={styles.altPaymentLogo}>
                  <path d="M35.2 1.5c-3.2 0-5.8 2.6-5.8 5.8v21.5c0 3.2 2.6 5.8 5.8 5.8h25.8c3.2 0 5.8-2.6 5.8-5.8V7.3c0-3.2-2.6-5.8-5.8-5.8H35.2z"/>
                  <path fill="#0A0B09" d="M61.8 17.1c-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5-1.1-2.5-2.5-2.5zm-8.3-5.3h2.7v11.9h-2.7V11.8zm-4.9 4.3c0-1-.8-1.7-1.9-1.7s-1.9.7-1.9 1.7v5.6h-2.7V11.8h2.6v.8c.6-.7 1.6-1.1 2.7-1.1 2.4 0 3.9 1.6 3.9 4v6.2h-2.7v-5.6zm-9.9-4.3h2.7v11.9h-2.7V11.8zm-5.7 6.3c0 1.2-.9 2.1-2.1 2.1s-2.1-.9-2.1-2.1.9-2.1 2.1-2.1 2.1.9 2.1 2.1zm2.7 0c0-2.7-2.1-4.7-4.8-4.7s-4.8 2.1-4.8 4.7 2.1 4.7 4.8 4.7 4.8-2 4.8-4.7z"/>
                  <path fill="#0A0B09" d="M14.4 17.7h2.9v6h-2.9zM14.4 11.7h2.9v3h-2.9zM11.5 11.7H8.6c0 2.5-.9 4.8-2.5 6.5l4.8 5.5h3.7l-4.3-5c1.3-2.1 2-4.5 2-7z"/>
                  <path fill="#0A0B09" d="M3 11.7H0v12h3z"/>
                </svg>
                <div className={styles.altPaymentInfo}>
                  <p className={styles.altPaymentName}>Klarna</p>
                  <p className={styles.altPaymentDesc}>Betal senere eller del opp</p>
                </div>
                <svg className={styles.altPaymentArrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            )}

            {hasVipps && (
              <button 
                className={styles.altPaymentButton}
                onClick={() => handleAltPayment('SVIPPS')}
                disabled={isProcessing}
              >
                <svg viewBox="0 0 72 18" height="24" className={styles.altPaymentLogo}>
                  <path fill="#FF5B24" d="M4.2 14.4L0 1.2h3.4l2.7 9 2.7-9h3.3l-4.2 13.2H4.2zm11.3 0V1.2h3.1v13.2h-3.1zm10.4-4.5v4.5h-3.1V1.2h5.5c2.6 0 4.5 1.6 4.5 4.3 0 2.8-1.9 4.4-4.5 4.4h-2.4zm2.1-2.4c1.1 0 1.7-.6 1.7-1.9 0-1.3-.6-1.9-1.7-1.9h-2.1v3.8h2.1zm9.6 2.4v4.5h-3.1V1.2h5.5c2.6 0 4.5 1.6 4.5 4.3 0 2.8-1.9 4.4-4.5 4.4h-2.4zm2.2-2.4c1.1 0 1.7-.6 1.7-1.9 0-1.3-.6-1.9-1.7-1.9h-2.2v3.8h2.2zm12.7.1c0 1-1.1 1.3-2.6 1.4-2.7.2-5.7.4-5.7 3.6 0 2.1 1.6 3.4 4 3.4 1.5 0 2.9-.5 3.9-1.6.1.5.2.9.4 1.4h3c-.3-.7-.5-1.6-.5-2.9V7.6c0-2.9-1.9-4.6-5-4.6-2.8 0-5.1 1.5-5.3 4.2h3c.1-1.2 1-1.9 2.3-1.9 1.4 0 2.1.7 2.1 1.6v.2h-.1c-.5 0-1.6.1-2.6.2-2.7.2-5.4.9-5.4 3.8 0 2.4 1.9 3.8 4.4 3.8 1.7 0 3.1-.6 4-1.8.1.6.2 1.2.4 1.6h3.1c-.3-.7-.5-1.6-.5-2.8V7.3c0-2.6-2.1-4.3-5.2-4.3-3 0-5.1 1.6-5.3 4.2h3c.1-1.1.9-1.8 2.2-1.8 1.4 0 2.1.6 2.1 1.5v.5h.3z"/>
                </svg>
                <div className={styles.altPaymentInfo}>
                  <p className={styles.altPaymentName}>Vipps</p>
                  <p className={styles.altPaymentDesc}>Betal enkelt med Vipps</p>
                </div>
                <svg className={styles.altPaymentArrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            )}

            {hasSwish && (
              <button 
                className={styles.altPaymentButton}
                onClick={() => handleAltPayment('NSWISH')}
                disabled={isProcessing}
              >
                <svg viewBox="0 0 48 48" height="24" className={styles.altPaymentLogo}>
                  <circle fill="#009CDB" cx="24" cy="24" r="24"/>
                  <path fill="#fff" d="M33.6 16.4c-2.4-2.4-6.2-2.4-8.6 0l-1.6 1.6c-.4.4-.4 1 0 1.4.4.4 1 .4 1.4 0l1.6-1.6c1.6-1.6 4.2-1.6 5.8 0s1.6 4.2 0 5.8l-5.8 5.8c-1.6 1.6-4.2 1.6-5.8 0-.4-.4-1-.4-1.4 0-.4.4-.4 1 0 1.4 2.4 2.4 6.2 2.4 8.6 0l5.8-5.8c2.4-2.4 2.4-6.2 0-8.6zm-9.2 15.2c-.4-.4-1-.4-1.4 0l-1.6 1.6c-1.6 1.6-4.2 1.6-5.8 0s-1.6-4.2 0-5.8l5.8-5.8c1.6-1.6 4.2-1.6 5.8 0 .4.4 1 .4 1.4 0 .4-.4.4-1 0-1.4-2.4-2.4-6.2-2.4-8.6 0l-5.8 5.8c-2.4 2.4-2.4 6.2 0 8.6 2.4 2.4 6.2 2.4 8.6 0l1.6-1.6c.4-.4.4-1 0-1.4z"/>
                </svg>
                <div className={styles.altPaymentInfo}>
                  <p className={styles.altPaymentName}>Swish</p>
                  <p className={styles.altPaymentDesc}>Betal med Swish</p>
                </div>
                <svg className={styles.altPaymentArrow} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            )}
          </div>
        </>
      )}

      <div className={styles.actions}>
        <Button
          variant="secondary"
          onClick={() => setStep('shipping')}
          className={styles.backButton}
        >
          Tilbake
        </Button>
      </div>

      <div className={styles.secureNotice}>
        <svg className={styles.lockIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
          <path d="M7 11V7a5 5 0 0110 0v4"/>
        </svg>
        Sikker betaling med SSL-kryptering
      </div>
    </div>
  );
}
